import React from "react";
import {
  useTable,
  useSortBy,
  useFilters,
  usePagination,
  useExpanded
} from "react-table";
import DropDownListFilter from "./DropDownListFilter";
import SelectInputFilter from "./SelectInputFilter";
import TextBoxFilter from "./TextBoxFilter";
import Button from "../Button";
import ColumnSelectorCheckbox from "./ColumnSelectorCheckbox";
import LinkButton from "../LinkButton";
import Tooltip from "../Tooltip";
import { useSticky } from "react-table-sticky";
import NewPagination from "../NewPagination";

import styles from "./DataTable.module.scss";

const defaultPropGetter = () => ({});

const DataTable = ({
  downloadCsvName = "",
  columns,
  data,
  csvColumns = null,
  hasFiltering = false,
  hasPagination = false,
  hasSorting = false,
  hasDownloadCsv = false,
  showColumnSelector = false,
  hasSubRows = false,
  hasHover = true,
  tooltipContent = "",
  tooltipPlacement = "bottom",
  isStripped = true,
  hiddenColumns = [],
  getHeaderProps = defaultPropGetter,
  getRowProps = defaultPropGetter,
  getCellProps = defaultPropGetter,
  maxWidth = null,
  ...rest
}) => {
  const [tableExpanded, setTableExpanded] = React.useState(false);
  const defaultColumn = React.useMemo(
    () => ({
      Filter: TextBoxFilter
    }),
    []
  );

  // WARN: the way of escaping commas and newlines is not universal because there isn't a single CSV spec
  const generateCsvLink = () => {
    let strCsv = "";
    let accessors = [];
    if (csvColumns === null) {
      columns.forEach(row => {
        strCsv += '"' + row.Header.replace(/"/g, '"""') + '",';
        accessors.push(row.accessor);
      });
    } else {
      csvColumns.forEach(row => {
        strCsv += '"' + row.Header.replace(/"/g, '""') + '",';
        accessors.push(row.accessor);
      });
    }
    data.forEach(row => {
      strCsv += "\n";
      accessors.forEach(accessor => {
        const val = String(row[accessor]);
        strCsv += '"' + val.replace(/"/g, '""') + '",';
      });
    });

    const url = URL.createObjectURL(new Blob([strCsv], { type: "text/csv" }));

    if (downloadCsvName) {
      /* Create not visble a href which allows us to name the file name */
      var a = document.createElement("a");
      document.body.appendChild(a);
      a.style = "display: none";
      a.href = url;
      a.download = downloadCsvName;
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } else {
      window.location.href = url;
    }
  };

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    nextPage,
    previousPage,
    allColumns,
    state: { pageIndex }
  } = useTable(
    {
      columns,
      data,
      defaultColumn,
      initialState: { hiddenColumns }
    },
    hasFiltering && useFilters,
    hasSorting && useSortBy,
    hasPagination && usePagination,
    hasSubRows && useExpanded,
    useSticky
  );

  return (
    <div className={styles.base}>
      {hasPagination && (
        <NewPagination
          resultsCount={data.length}
          pageIndex={pageIndex}
          pageOptions={pageOptions}
          canPreviousPage={canPreviousPage}
          canNextPage={canNextPage}
          previousPage={previousPage}
          nextPage={nextPage}
        />
      )}
      <div className={styles.table}>
        {showColumnSelector ? (
          <div className={styles.toggleButton}>
            <LinkButton
              icon={tableExpanded ? "contract" : "expand"}
              style={{ outline: "#fff", marginLeft: "auto" }}
              onClick={() => setTableExpanded(!tableExpanded)}
            />
          </div>
        ) : null}
        <div
          className={`${styles.wrapper} ${
            tableExpanded ? styles.wrapperXl : ""
          } ${rest.className ? rest.className : ""}`}
          style={{ maxWidth: maxWidth }}
        >
          <table {...getTableProps()} className={styles.sticky}>
            <thead>
              {headerGroups.map(headerGroup => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map(column => (
                    <th
                      {...column.getHeaderProps([
                        hasSorting ? column.getSortByToggleProps() : "",
                        {
                          style: column.style
                        },
                        getHeaderProps(column)
                      ])}
                    >
                      {column.render("Header")}
                      <span>
                        {column.Header === "Actions" && tooltipContent ? (
                          <Tooltip placement={tooltipPlacement}>
                            {tooltipContent}
                          </Tooltip>
                        ) : null}
                        {column.canSort ? (
                          column.isSorted ? (
                            column.isSortedDesc ? (
                              <span className={styles.iconCaretUp} />
                            ) : (
                              <span className={styles.iconCaretDown} />
                            )
                          ) : (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              viewBox="0 0 16 16"
                            >
                              <path d="M4.55 7.15l3.4-4.6 3.45 4.6z"></path>
                              <path d="M11.4 8.65l-3.45 4.65-3.4-4.65z"></path>
                            </svg>
                          )
                        ) : (
                          " "
                        )}
                      </span>
                      {column.canFilter && (
                        <div onClick={event => event.stopPropagation()}>
                          {column.render("Filter", column.filterProps)}
                        </div>
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()} className={styles.body}>
              {data.length === 0 ? (
                <tr>
                  <td style={{ textAlign: "center" }} colSpan={columns.length}>
                    No data to show
                  </td>
                </tr>
              ) : (
                (hasPagination ? page : rows).map((row, i) => {
                  prepareRow(row);
                  return (
                    <tr
                      {...row.getRowProps([getRowProps(row)])}
                      className={`${hasHover ? styles.hasHover : ""} ${
                        isStripped ? styles.stripped : ""
                      }`}
                    >
                      {row.cells.map(cell => {
                        return (
                          <td
                            {...cell.getCellProps([
                              { style: cell.column.style },
                              getCellProps(cell)
                            ])}
                          >
                            {cell.render("Cell")}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {hasDownloadCsv && (
          <div className={styles.downloadCsv}>
            <Button
              className={styles.button}
              primary
              disabled={data.length === 0}
              onClick={() => generateCsvLink()}
            >
              Download CSV
            </Button>
          </div>
        )}{" "}
      </div>
      {showColumnSelector ? (
        <ColumnSelectorCheckbox columns={allColumns} isHidden={tableExpanded} />
      ) : null}
    </div>
  );
};

export {
  DataTable as default,
  DropDownListFilter,
  SelectInputFilter,
  TextBoxFilter
};
